import { ImageIcon, Plus, TrashIcon } from "@/components/icons";
import {
  Badge,
  Button,
  Card,
  CardContent,
  DatePicker,
  Input,
  Label,
  Separator,
} from "@/components/kit";
import {
  INVENTORY_CARRIERS,
  SHIPPING_ALLOCATION_METHODS,
  type CreateGoodsReceiptDTO,
} from "@/modules/inventory/domain/contracts";
import { calculateLandedCosts } from "@/modules/inventory/domain/service/cost-allocation";
import {
  ProductPickerModal,
  type PickedVariant,
} from "@/modules/orders/presentation/ui/ProductPickerModal";
import {
  convertReceiveQtyToStock,
  formatQtyWithUnit,
  usesAlternateReceiveUnit,
} from "@/modules/products/domain/unit-conversion";
import { formatLak } from "@/shared/lib/currency";
import { formatDateForInput } from "@/shared/lib/date-time";
import { AppImage } from "@/shared/ui/AppImage";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { useMemo, useState } from "react";

export type ReceiptLineForm = {
  variantId: string;
  productName: string;
  variantName: string;
  imageKey: string | null;
  quantity: number;
  unitPurchaseCost: number;
  shippingAllocated?: number;
  stockUnitLabel: string;
  receiveUnitLabel: string | null;
  receiveConversionFactor: number;
};

export type ReceiptFormValues = {
  receivedAt: string;
  carrier: string;
  carrierNote: string;
  trackingNumber: string;
  shippingFeeTotal: number;
  otherFeesTotal: number;
  shippingAllocationMethod: (typeof SHIPPING_ALLOCATION_METHODS)[number];
  note: string;
  lines: ReceiptLineForm[];
};

const CARRIER_LABELS: Record<string, string> = {
  hung_arun: "ຮຸ່ງອາລຸນ",
  anusith: "ອານຸສິດ",
  other: "ອື່ນໆ",
};

const ALLOCATION_LABELS: Record<string, string> = {
  by_value: "ຕາມມູນຄ່າສິນຄ້າ",
  by_quantity: "ຕາມຈຳນວນ",
  manual: "ໃສ່ເອງຕໍ່ລາຍການ",
};

function emptyValues(): ReceiptFormValues {
  return {
    receivedAt: formatDateForInput(new Date()),
    carrier: "hung_arun",
    carrierNote: "",
    trackingNumber: "",
    shippingFeeTotal: 0,
    otherFeesTotal: 0,
    shippingAllocationMethod: "by_value",
    note: "",
    lines: [],
  };
}

type GoodsReceiptFormProps = {
  defaultValues?: Partial<ReceiptFormValues>;
  submitting?: boolean;
  submitLabel?: string;
  onSubmit: (values: CreateGoodsReceiptDTO) => Promise<void> | void;
};

export function GoodsReceiptForm({
  defaultValues,
  submitting,
  submitLabel = "ບັນທຶກ draft",
  onSubmit,
}: GoodsReceiptFormProps) {
  const [values, setValues] = useState<ReceiptFormValues>({
    ...emptyValues(),
    ...defaultValues,
    lines: defaultValues?.lines ?? [],
  });
  const [pickerOpen, setPickerOpen] = useState(false);

  const preview = useMemo(
    () =>
      calculateLandedCosts({
        shippingFeeTotal: values.shippingFeeTotal,
        otherFeesTotal: values.otherFeesTotal,
        shippingAllocationMethod: values.shippingAllocationMethod,
        lines: values.lines.map((l) => ({
          variantId: l.variantId,
          quantity: l.quantity,
          unitPurchaseCost: l.unitPurchaseCost,
          shippingAllocated: l.shippingAllocated,
        })),
      }),
    [values],
  );

  function patch(patchValues: Partial<ReceiptFormValues>) {
    setValues((v) => ({ ...v, ...patchValues }));
  }

  function handlePick(picked: PickedVariant) {
    setValues((v) => {
      const existing = v.lines.find((l) => l.variantId === picked.variantId);
      if (existing) {
        return {
          ...v,
          lines: v.lines.map((l) =>
            l.variantId === picked.variantId
              ? { ...l, quantity: l.quantity + 1 }
              : l,
          ),
        };
      }
      return {
        ...v,
        lines: [
          ...v.lines,
          {
            variantId: picked.variantId,
            productName: picked.productName,
            variantName: picked.variantName,
            imageKey: picked.imageKey,
            quantity: 1,
            unitPurchaseCost: 0,
            stockUnitLabel: picked.stockUnitLabel,
            receiveUnitLabel: picked.receiveUnitLabel,
            receiveConversionFactor:
              Number(picked.receiveConversionFactor) || 1,
          },
        ],
      };
    });
  }

  function updateLine(variantId: string, patchLine: Partial<ReceiptLineForm>) {
    setValues((v) => ({
      ...v,
      lines: v.lines.map((l) =>
        l.variantId === variantId ? { ...l, ...patchLine } : l,
      ),
    }));
  }

  function removeLine(variantId: string) {
    setValues((v) => ({
      ...v,
      lines: v.lines.filter((l) => l.variantId !== variantId),
    }));
  }

  const canSubmit =
    values.lines.length > 0 &&
    values.lines.every((l) => l.quantity > 0 && l.unitPurchaseCost >= 0) &&
    !submitting;

  async function handleSubmit() {
    if (!canSubmit) return;
    const payload: CreateGoodsReceiptDTO = {
      receivedAt: new Date(values.receivedAt).toISOString(),
      carrier: (values.carrier || null) as CreateGoodsReceiptDTO["carrier"],
      carrierNote: values.carrierNote || null,
      trackingNumber: values.trackingNumber || null,
      shippingFeeTotal: values.shippingFeeTotal,
      otherFeesTotal: values.otherFeesTotal,
      shippingAllocationMethod: values.shippingAllocationMethod,
      note: values.note || null,
      lines: values.lines.map((l) => ({
        variantId: l.variantId,
        quantity: l.quantity,
        unitPurchaseCost: l.unitPurchaseCost,
        shippingAllocated:
          values.shippingAllocationMethod === "manual"
            ? (l.shippingAllocated ?? 0)
            : undefined,
        productName: l.productName,
        variantName: l.variantName,
      })),
    };
    await onSubmit(payload);
  }

  const previewByVariant = new Map(preview.lines.map((l) => [l.variantId, l]));

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <Card className="brand-card">
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="receivedAt">ວັນທີຮັບເຂົ້າ</Label>
            <DatePicker
              value={values.receivedAt ? new Date(values.receivedAt) : null}
              onChange={(date) =>
                patch({
                  receivedAt: date ? formatDateForInput(date) : "",
                })
              }
              captionLayout="dropdown"
              fromYear={2024}
              toYear={new Date().getFullYear() + 1}
              className="w-full"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>ຂົນສົ່ງ</Label>
            <SimpleSelect
              value={values.carrier}
              onValueChange={(val) => patch({ carrier: val })}
              options={INVENTORY_CARRIERS.map((c) => ({
                value: c,
                label: CARRIER_LABELS[c] ?? c,
              }))}
              placeholder="ເລືອກຂົນສົ່ງ"
            />
          </div>
          {values.carrier === "other" ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="carrierNote">ຊື່ຂົນສົ່ງ</Label>
              <Input
                id="carrierNote"
                value={values.carrierNote}
                onChange={(e) => patch({ carrierNote: e.target.value })}
                placeholder="ຊື່ບໍລິສັດຂົນສົ່ງ"
              />
            </div>
          ) : null}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="trackingNumber">ເລກ Tracking (ຖ້າມີ)</Label>
            <Input
              id="trackingNumber"
              value={values.trackingNumber}
              onChange={(e) => patch({ trackingNumber: e.target.value })}
              placeholder="ເລກພັດສະດຸ"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="shippingFee">ຄ່າຂົນສົ່ງລວມ (LAK)</Label>
            <Input
              id="shippingFee"
              type="number"
              inputMode="numeric"
              min={0}
              value={values.shippingFeeTotal || ""}
              onChange={(e) =>
                patch({ shippingFeeTotal: Number(e.target.value) || 0 })
              }
              placeholder="0"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="otherFees">ຄ່າອື່ນໆ (LAK)</Label>
            <Input
              id="otherFees"
              type="number"
              inputMode="numeric"
              min={0}
              value={values.otherFeesTotal || ""}
              onChange={(e) =>
                patch({ otherFeesTotal: Number(e.target.value) || 0 })
              }
              placeholder="0"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>ວິທີແບ່ງຄ່າຂົນສົ່ງ</Label>
            <SimpleSelect
              value={values.shippingAllocationMethod}
              onValueChange={(val) =>
                patch({
                  shippingAllocationMethod:
                    val as ReceiptFormValues["shippingAllocationMethod"],
                })
              }
              options={SHIPPING_ALLOCATION_METHODS.map((m) => ({
                value: m,
                label: ALLOCATION_LABELS[m] ?? m,
              }))}
            />
          </div>
          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <Label htmlFor="note">ໝາຍເຫດ</Label>
            <Input
              id="note"
              value={values.note}
              onChange={(e) => patch({ note: e.target.value })}
              placeholder="ໝາຍເຫດເພີ່ມເຕີມ"
            />
          </div>
        </CardContent>
      </Card>

      {/* Lines */}
      <Card className="brand-card">
        <CardContent className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg">ລາຍການສິນຄ້າ</h3>
            <Button
              type="button"
              variant="outline"
              className="rounded-full border-secondary/40"
              onClick={() => setPickerOpen(true)}
            >
              <Plus data-icon="inline-start" />
              ເພີ່ມສິນຄ້າ
            </Button>
          </div>

          {values.lines.length === 0 ? (
            <p className="py-6 text-center text-muted-foreground text-sm">
              ຍັງບໍ່ມີລາຍການ — ກົດ «ເພີ່ມສິນຄ້າ» ເພື່ອເລືອກ
            </p>
          ) : (
            <ul className="flex flex-col gap-3">
              {values.lines.map((line) => {
                const calc = previewByVariant.get(line.variantId);
                const hasAltUnit = usesAlternateReceiveUnit(line);
                const receiveUnit =
                  line.receiveUnitLabel?.trim() || line.stockUnitLabel;
                const stockQty = convertReceiveQtyToStock(
                  line.quantity,
                  line.receiveConversionFactor,
                );
                const unitLandedStock =
                  calc && stockQty > 0 ? calc.lineLandedTotal / stockQty : null;

                return (
                  <li
                    key={line.variantId}
                    className="rounded-xl border border-secondary/25 bg-background p-3"
                  >
                    <div className="flex items-start gap-3">
                      <div className="size-12 shrink-0 overflow-hidden rounded-lg bg-muted/40">
                        {line.imageKey ? (
                          <AppImage
                            src={line.imageKey}
                            alt={line.productName}
                            className="size-full"
                            fit="cover"
                          />
                        ) : (
                          <div className="flex size-full items-center justify-center text-muted-foreground">
                            <ImageIcon className="opacity-40" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm leading-snug">
                          {line.productName}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          {line.variantName}
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-8 shrink-0 text-muted-foreground hover:text-destructive"
                        onClick={() => removeLine(line.variantId)}
                      >
                        <TrashIcon />
                      </Button>
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <div className="flex flex-col gap-1">
                        <Label className="text-xs">ຈຳນວນ ({receiveUnit})</Label>
                        <Input
                          type="number"
                          inputMode="numeric"
                          min={1}
                          value={line.quantity || ""}
                          onChange={(e) =>
                            updateLine(line.variantId, {
                              quantity: Math.max(
                                0,
                                Math.trunc(Number(e.target.value) || 0),
                              ),
                            })
                          }
                        />
                        {hasAltUnit ? (
                          <p className="text-muted-foreground text-xs">
                            → {formatQtyWithUnit(stockQty, line.stockUnitLabel)}{" "}
                            ເຂົ້າສະຕັອກ
                          </p>
                        ) : null}
                      </div>
                      <div className="flex flex-col gap-1">
                        <Label className="text-xs">
                          ຕົ້ນທຸນ/{receiveUnit} (LAK)
                        </Label>
                        <Input
                          type="number"
                          inputMode="numeric"
                          min={0}
                          value={line.unitPurchaseCost || ""}
                          onChange={(e) =>
                            updateLine(line.variantId, {
                              unitPurchaseCost: Number(e.target.value) || 0,
                            })
                          }
                        />
                      </div>
                      {values.shippingAllocationMethod === "manual" ? (
                        <div className="col-span-2 flex flex-col gap-1">
                          <Label className="text-xs">ຄ່າຂົນສົ່ງລາຍການນີ້ (LAK)</Label>
                          <Input
                            type="number"
                            inputMode="numeric"
                            min={0}
                            value={line.shippingAllocated || ""}
                            onChange={(e) =>
                              updateLine(line.variantId, {
                                shippingAllocated: Number(e.target.value) || 0,
                              })
                            }
                          />
                        </div>
                      ) : null}
                    </div>

                    {calc ? (
                      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                        <span className="text-muted-foreground">
                          ຂົນສົ່ງ: {formatLak(calc.shippingAllocated)}
                        </span>
                        {hasAltUnit && unitLandedStock != null ? (
                          <Badge
                            variant="outline"
                            className="border-secondary/40 text-secondary"
                          >
                            landed {formatLak(unitLandedStock)}/
                            {line.stockUnitLabel}
                          </Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className="border-secondary/40 text-secondary"
                          >
                            landed {formatLak(calc.unitLandedCost)}/
                            {receiveUnit}
                          </Badge>
                        )}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Summary */}
      <Card className="brand-card">
        <CardContent className="flex flex-col gap-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">ຕົ້ນທຸນສິນຄ້າລວມ</span>
            <span className="tabular-nums">
              {formatLak(preview.purchaseSubtotal)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">ຄ່າຂົນສົ່ງ + ຄ່າອື່ນ</span>
            <span className="tabular-nums">
              {formatLak(preview.shippingFeeTotal + preview.otherFeesTotal)}
            </span>
          </div>
          <Separator />
          <div className="flex justify-between font-semibold">
            <span>ຕົ້ນທຸນລວມ (landed)</span>
            <span className="text-secondary tabular-nums">
              {formatLak(preview.landedSubtotal)}
            </span>
          </div>
          {preview.warning ? (
            <p className="mt-1 rounded-lg bg-destructive/10 px-3 py-2 text-destructive text-xs">
              {preview.warning}
            </p>
          ) : null}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button
          type="button"
          disabled={!canSubmit}
          onClick={handleSubmit}
          className="rounded-full"
        >
          {submitLabel}
        </Button>
      </div>

      <ProductPickerModal
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onPick={handlePick}
        allowZeroStock
        purpose="receive"
        subtitle="ກົດທີ່ຕົວເລືອກເພື່ອເພີ່ມໃນໃບຮັບເຂົ້າ"
      />
    </div>
  );
}
